import type { Appointment, Reminder } from '@secretario/shared';

/**
 * O que o aparelho deve avisar a partir da agenda: cada lembrete na hora dele
 * e cada compromisso nos alertas que a pessoa escolheu ("na hora", "10 min
 * antes", "1 dia antes"). Regra pura, sem tocar no aparelho.
 */

export type AgendaNotice = {
  key: string;
  title: string;
  body: string;
  at: Date;
  route: string;
  mode?: 'notice' | 'alarm';
  /** Ler em voz alta (lembrete falado). */
  speak?: boolean;
};

/** Compromisso antigo, de antes dos alertas: avisa 15 minutos antes. */
export const DEFAULT_ALERT_MINUTES = 15;
/** Dia inteiro não tem hora: o aviso "na hora" sai de manhã. */
export const ALL_DAY_HOUR = 8;
export const REMINDER_PREFIX = 'lembrete:';

const clock = (date: Date) =>
  date.getMinutes() === 0
    ? `${date.getHours()}h`
    : `${date.getHours()}h${String(date.getMinutes()).padStart(2, '0')}`;

/** "Começa agora.", "Começa em 10 minutos.", "É amanhã, às 15h." */
export function leadText(minutes: number, start: Date, allDay: boolean): string {
  if (allDay) {
    if (minutes === 0) return 'É hoje, o dia todo.';
    if (minutes === 1440) return 'É amanhã, o dia todo.';
    return minutes % 1440 === 0 ? `É daqui a ${minutes / 1440} dias.` : 'Está chegando.';
  }
  if (minutes === 0) return 'Começa agora.';
  if (minutes < 60) return `Começa em ${minutes} minutos.`;
  if (minutes === 60) return 'Começa em 1 hora.';
  if (minutes === 1440) return `É amanhã, às ${clock(start)}.`;
  if (minutes % 1440 === 0) return `É daqui a ${minutes / 1440} dias, às ${clock(start)}.`;
  if (minutes % 60 === 0) return `Começa em ${minutes / 60} horas.`;
  return `Começa às ${clock(start)}.`;
}

export function planNotices(
  reminders: Reminder[],
  appointments: Appointment[],
  options: { alarm?: boolean; speak?: boolean } = {},
): AgendaNotice[] {
  const planned: AgendaNotice[] = [];
  for (const reminder of reminders) {
    if (reminder.status !== 'pending' || reminder.deletedAt) continue;
    planned.push({
      key: `${REMINDER_PREFIX}${reminder.id}`,
      title: 'Lembrete',
      body: reminder.message,
      at: new Date(reminder.remindAt),
      route: '/agenda',
      // O modo despertador vale para o lembrete, que é o que a pessoa pediu para não perder.
      ...(options.alarm ? { mode: 'alarm' as const } : {}),
      ...(options.speak ? { speak: true } : {}),
    });
  }
  for (const appointment of appointments) {
    if (appointment.status !== 'scheduled' || appointment.deletedAt) continue;
    const start = new Date(appointment.startsAt);
    const allDay = appointment.allDay === true;
    const base = allDay
      ? new Date(start.getFullYear(), start.getMonth(), start.getDate(), ALL_DAY_HOUR)
      : start;
    const alerts = [...new Set(appointment.alerts ?? [DEFAULT_ALERT_MINUTES])];
    const who = appointment.contactName?.trim() ? `Com ${appointment.contactName.trim()}. ` : '';
    for (const minutes of alerts) {
      planned.push({
        key: `compromisso:${appointment.id}:${minutes}`,
        title: allDay ? appointment.title : `${appointment.title} às ${clock(start)}`,
        body: `${who}${leadText(minutes, start, allDay)}`,
        at: new Date(base.getTime() - minutes * 60_000),
        route: '/agenda',
        ...(options.speak ? { speak: true } : {}),
      });
    }
  }
  return planned;
}

/** Os lembretes (pelo id do servidor) que estão entre os avisos agendados. */
export function reminderIdsOf(keys: string[]): string[] {
  return keys
    .filter((key) => key.startsWith(REMINDER_PREFIX))
    .map((key) => key.slice(REMINDER_PREFIX.length));
}
